import React from 'react';
import { useStyles } from '@archipro-design/aria';
import * as S from './ColorCarousel.mobile.style';
import type AttributeOption from '@modules/product/type/attribute-option';
import ColorOption from '../color-selector-popup/ColorOption';
import { useIsomorphicLayoutEffect } from 'ahooks';

interface Props {
    colors: AttributeOption[];
    selectedID?: number;
    onSelect: (option: AttributeOption) => void;
    visible: boolean;
}

const ColorCarousel = ({ colors, selectedID, onSelect, visible }: Props) => {
    const { css } = useStyles({ visible });
    const ref = React.useRef<HTMLDivElement>(null);
    const shouldAnimateRef = React.useRef(false);

    useIsomorphicLayoutEffect(() => {
        const container = ref.current;
        if (container) {
            const selected = container.querySelector(
                '[aria-selected="true"]'
            ) as HTMLElement;
            const leftPadding = 30;
            const rightPadding = 30;

            if (!selected || !container || !container.scrollTo) {
                return;
            }

            const anchorLeft =
                selected.offsetLeft - container.scrollLeft - leftPadding;
            if (anchorLeft < 0) {
                container.scrollTo({
                    left: Math.max(container.scrollLeft + anchorLeft, 0),
                    behavior: shouldAnimateRef.current ? 'smooth' : undefined,
                });
            } else {
                const rightSpace =
                    container.offsetWidth -
                    anchorLeft -
                    selected.offsetWidth -
                    leftPadding -
                    rightPadding;

                if (rightSpace < 0) {
                    container.scrollTo({
                        left: Math.max(container.scrollLeft - rightSpace, 0),
                        behavior: shouldAnimateRef.current
                            ? 'smooth'
                            : undefined,
                    });
                }
            }
        }
    }, [ref, selectedID, shouldAnimateRef]);

    const onOptionSelect = (option: AttributeOption) => {
        shouldAnimateRef.current = true;
        onSelect(option);
    };

    return (
        <div className={css(S.ColorCarousel)} ref={ref}>
            <div className={css(S.ColorCarouselInner)}>
                {colors.map((c) => (
                    <div key={'color-' + c.ID}>
                        <ColorOption
                            data={{
                                option: c,
                            }}
                            onSelect={onOptionSelect}
                            selected={c.ID === selectedID}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ColorCarousel;
